import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useLang } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Trash2, ImagePlus } from "lucide-react";

export default function RestaurantSettings() {
  const { id } = useParams();
  const { t } = useLang();
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/restaurants/${id}`);
    setR(data);
  };
  useEffect(() => { load(); }, [id]);

  const fileToBase64 = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });

  const handleFile = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const b64 = await fileToBase64(file);
    setR({ ...r, [key]: b64 });
  };

  const save = async () => {
    setBusy(true);
    try {
      const { theme, owner_id, slug, id: rid, created_at, ...payload } = r;
      await api.put(`/restaurants/${id}`, payload);
      toast.success(t("common.success"));
    } catch (err) {
      toast.error("Error");
    } finally { setBusy(false); }
  };

  const removeR = async () => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/restaurants/${id}`);
    window.location.href = "/dashboard";
  };

  if (!r) return <DashboardLayout restaurantId={id}><div>{t("common.loading")}</div></DashboardLayout>;

  return (
    <DashboardLayout restaurantId={id}>
      <div className="space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{t("common.settings")}</div>
          <h1 className="text-3xl font-bold tracking-tight">{r.name}</h1>
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border bg-card p-5 space-y-4">
            <div className="space-y-1.5">
              <Label>{t("restaurant.restaurantName")}</Label>
              <Input value={r.name} onChange={(e) => setR({ ...r, name: e.target.value })} className="rounded-xl" data-testid="settings-name" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("restaurant.description")}</Label>
              <Textarea value={r.description} onChange={(e) => setR({ ...r, description: e.target.value })} className="rounded-xl" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t("restaurant.currency")}</Label>
                <select className="w-full h-10 rounded-xl border bg-background px-3" value={r.currency} onChange={(e) => setR({ ...r, currency: e.target.value })}>
                  <option value="EUR">EUR €</option>
                  <option value="USD">USD $</option>
                  <option value="TRY">TRY ₺</option>
                  <option value="AED">AED د.إ</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("restaurant.phone")}</Label>
                <Input value={r.phone || ""} onChange={(e) => setR({ ...r, phone: e.target.value })} className="rounded-xl" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("restaurant.address")}</Label>
              <Input value={r.address || ""} onChange={(e) => setR({ ...r, address: e.target.value })} className="rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("restaurant.website")}</Label>
              <Input value={r.website || ""} onChange={(e) => setR({ ...r, website: e.target.value })} className="rounded-xl" />
            </div>
          </div>

          <div className="rounded-2xl border bg-card p-5 space-y-4">
            <div>
              <Label className="mb-2 block">{t("restaurant.logo")}</Label>
              <div className="flex items-center gap-3">
                <div className="w-20 h-20 rounded-2xl border bg-secondary grid place-items-center overflow-hidden">
                  {r.logo ? <img src={r.logo} alt="logo" className="w-full h-full object-cover" /> : <ImagePlus className="h-6 w-6 text-muted-foreground" />}
                </div>
                <label className="cursor-pointer">
                  <Input type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e, "logo")} data-testid="logo-upload" />
                  <Button type="button" variant="outline" className="rounded-full pointer-events-none">{t("restaurant.uploadLogo")}</Button>
                </label>
              </div>
            </div>
            <div>
              <Label className="mb-2 block">{t("restaurant.coverImage")}</Label>
              <div className="space-y-3">
                <div className="aspect-[16/7] w-full rounded-2xl border bg-secondary overflow-hidden">
                  {r.cover_image ? <img src={r.cover_image} alt="cover" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center"><ImagePlus className="h-6 w-6 text-muted-foreground" /></div>}
                </div>
                <label className="cursor-pointer">
                  <Input type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e, "cover_image")} data-testid="cover-upload" />
                  <Button type="button" variant="outline" className="rounded-full pointer-events-none">{t("restaurant.uploadCover")}</Button>
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-between gap-3">
          <Button variant="outline" onClick={removeR} className="rounded-full text-destructive hover:text-destructive" data-testid="delete-restaurant">
            <Trash2 className="h-4 w-4 mr-2" />
            {t("common.delete")}
          </Button>
          <Button onClick={save} disabled={busy} className="rounded-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-500/25" data-testid="save-settings">
            {t("common.save")}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
